import { Badge, Text, Tooltip, Group, Progress } from '@mantine/core';
import type { DataLibrarySurveyRow, DataLibrarySurveyQuestion, ParticipantFlagCategory } from '../../../types';
import { SurveyResponsePill } from '../components/SurveyResponsePill';
import { ParticipantEmailPill } from '../components/ParticipantEmailPill';
import { GroupBadge } from '../../common';
import { getAttendanceCoverageDisplay } from '../../../utils/attendanceCoverage';
import { renderCohortHealthBadge } from '../components/CohortHealthBadge';
import { FLAG_SEVERITY, getFlagSeverity } from '../../../config/severityConfig';

export function renderSurveyCell(
  row: DataLibrarySurveyRow,
  columnId: string,
  questions: DataLibrarySurveyQuestion[],
): React.ReactNode {
  switch (columnId) {
    case 'email':
      return (
        <ParticipantEmailPill
          email={row.email}
          participantId={row.participant_id}
          cohortId={row.cohort_id}
        />
      );
    case 'first_name':
      return <Text size="sm">{row.first_name}</Text>;
    case 'last_name':
      return <Text size="sm">{row.last_name}</Text>;
    case 'full_name':
      return <Text size="sm">{row.full_name}</Text>;
    case 'attendance_rate': {
      if (row.attendance_rate === null) {
        return <Text size="sm" c="dimmed">—</Text>;
      }
      const pct = row.attendance_rate;
      const color = pct >= 80 ? 'medleyGreen' : pct >= 60 ? 'orange' : 'dangerRed';
      return (
        <Tooltip label={`${pct}% attendance`} withArrow>
          <Group gap={6} wrap="nowrap">
            <Progress value={pct} color={color} size="sm" w={50} />
            <Text size="xs" c="dimmed">{pct}%</Text>
          </Group>
        </Tooltip>
      );
    }
    case 'avg_engagement':
      return (
        <Text size="sm">
          {row.avg_engagement !== null ? Number(row.avg_engagement).toFixed(1) : <>&mdash;</>}
        </Text>
      );
    case 'attendance_coverage': {
      // MED-1266 (AC12): marking coverage column. Survey rows may not carry coverage
      // from the API yet — guard for undefined and render "—".
      if (row.attendance_marked === undefined || row.attendance_population === undefined) {
        return <Text size="sm" c="dimmed">—</Text>;
      }
      const d = getAttendanceCoverageDisplay({
        rate: 1,
        marked: row.attendance_marked,
        population: row.attendance_population,
      });
      return <Text size="sm">{d.isUnrecorded ? '—' : d.isComplete ? 'Complete' : d.coverageLabel}</Text>;
    }
    case 'group_name':
      return <GroupBadge groupName={row.group_name} groupId={row.group_id} />;
    case 'coach_name':
      return <Text size="sm">{row.coach_name || '—'}</Text>;
    case 'submission_id':
      return (
        <Text size="xs" c="dimmed" ff="monospace">
          {row.submission_id.slice(0, 12)}...
        </Text>
      );
    case 'survey_stage':
      return <Text size="sm">{row.survey_stage}</Text>;
    case 'overall_satisfaction':
      if (!row.overall_satisfaction) {
        return <Text size="xs" c="dimmed">—</Text>;
      }
      return <SurveyResponsePill value={row.overall_satisfaction} questionType="scale" />;
    case 'cohort_name':
      return <Text size="sm">{row.cohort_name}</Text>;
    case 'cohort_health':
      return renderCohortHealthBadge(row.cohort_health);
    case 'has_open_flag': {
      if (!row.flags || row.flags.length === 0) {
        return <Text size="sm" c="dimmed">&mdash;</Text>;
      }
      return (
        <Group gap={4} wrap="nowrap">
          {row.flags.slice(0, 3).map(flag => {
            const severity = getFlagSeverity(flag.flag_category as ParticipantFlagCategory);
            const config = FLAG_SEVERITY[severity];
            return (
              <Tooltip key={flag.id} label={flag.flag_reason} withArrow>
                <Badge
                  size="xs"
                  variant="outline"
                  styles={{
                    root: {
                      borderColor: config.pill.border,
                      color: config.pill.text,
                      backgroundColor: config.pill.background,
                    },
                  }}
                >
                  {flag.flag_category === 'coach_flag'
                    ? 'Coach'
                    : flag.flag_category === 'attendance'
                      ? 'Attend'
                      : flag.flag_category === 'participant_feedback'
                        ? 'Feedback'
                        : 'Engage'}
                </Badge>
              </Tooltip>
            );
          })}
          {row.flags.length > 3 && (
            <Text size="xs" c="dimmed">+{row.flags.length - 3}</Text>
          )}
        </Group>
      );
    }
    default: {
      // Dynamic question columns (prefixed with q_)
      if (columnId.startsWith('q_')) {
        const questionId = columnId.slice(2);
        const value = row.responses[questionId];
        const question = questions.find(q => q.question_id === questionId);

        if (!value) {
          return <Text size="xs" c="dimmed">—</Text>;
        }

        return (
          <SurveyResponsePill
            value={value}
            questionType={question?.question_type || 'text'}
          />
        );
      }
      return <Text size="sm">—</Text>;
    }
  }
}
